import { useState, useCallback } from 'react';
import { Button, Input, Spinner } from '../ui';
import { useCreateMailbox, useStaff, type CreateMailboxDto, type SmtpSecurityMode, type TestSmtpResult } from '../../api/mailboxes';
import { useAuth } from '../../context/AuthContext';
import { canManageMailboxOwner } from '../../lib/mailbox-roles';
import { SmtpSettings, initialSmtpState, type SmtpFormState } from './SmtpSettings';
import { getErrorMessage } from '../../lib/error-messages';
import { client } from '../../api/client';

interface AddMailboxFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormState {
  email: string;
  name: string;
  imapHost: string;
  imapPort: string;
  imapUser: string;
  imapPassword: string;
  imapTls: boolean;
}

const initialState: FormState = {
  email: '',
  name: '',
  imapHost: '',
  imapPort: '993',
  imapUser: '',
  imapPassword: '',
  imapTls: true,
};

const gmailPreset: Partial<FormState> = {
  imapHost: 'imap.gmail.com',
  imapPort: '993',
  imapTls: true,
};

export function AddMailboxForm({ onSuccess, onCancel }: AddMailboxFormProps): JSX.Element {
  const [form, setForm] = useState<FormState>(initialState);
  const [smtpForm, setSmtpForm] = useState<SmtpFormState>(initialSmtpState);
  const [showSmtp, setShowSmtp] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState | keyof SmtpFormState, string>>>({});
  const [createdMailboxId, setCreatedMailboxId] = useState<string | null>(null);
  const [smtpWarning, setSmtpWarning] = useState<string | null>(null);

  // Владельца выбирает только тот, кто видит все ящики организации.
  // Менеджер подключает ящик себе, и поле ему не показывается: сервер
  // всё равно поставит его самого, а тело запроса игнорирует
  const { user } = useAuth();
  const canChooseOwner = canManageMailboxOwner(user?.role);
  const { data: staff, isError: staffFailed } = useStaff(
    canChooseOwner,
    user?.orgId ?? undefined
  );
  const [ownerId, setOwnerId] = useState<string>('');
  const createMailbox = useCreateMailbox();

  const updateField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const applyGmailPreset = () => {
    setForm((prev) => ({
      ...prev,
      ...gmailPreset,
      imapUser: prev.email || prev.imapUser,
    }));
  };

  const updateSmtpField = useCallback((field: keyof SmtpFormState, value: string | SmtpSecurityMode) => {
    setSmtpForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const handleTestSmtp = useCallback(async (): Promise<TestSmtpResult> => {
    // For new mailbox, we need to create it first, then test SMTP
    // Or we could test with a temporary endpoint... For now, test after creation
    if (!createdMailboxId) {
      return { ok: false, error: 'Сначала создайте почтовый ящик' };
    }

    const response = await client.post<TestSmtpResult>(`/mailboxes/${createdMailboxId}/test-smtp`, {
      host: smtpForm.smtpHost.trim(),
      port: parseInt(smtpForm.smtpPort, 10) || 587,
      securityMode: smtpForm.smtpSecurityMode,
      user: smtpForm.smtpUser.trim(),
      password: smtpForm.smtpPassword,
    });
    return response.data;
  }, [createdMailboxId, smtpForm]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};

    if (!form.email.trim()) {
      newErrors.email = 'Email обязателен';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Неверный формат email';
    }

    if (!form.name.trim()) {
      newErrors.name = 'Название обязательно';
    }

    if (!form.imapHost.trim()) {
      newErrors.imapHost = 'IMAP хост обязателен';
    }

    const port = parseInt(form.imapPort, 10);
    if (!form.imapPort.trim() || isNaN(port) || port < 1 || port > 65535) {
      newErrors.imapPort = 'Порт должен быть от 1 до 65535';
    }

    if (!form.imapUser.trim()) {
      newErrors.imapUser = 'IMAP пользователь обязателен';
    }

    if (!form.imapPassword.trim()) {
      newErrors.imapPassword = 'IMAP пароль обязателен';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const data: CreateMailboxDto = {
      email: form.email.trim(),
      name: form.name.trim(),
      imapHost: form.imapHost.trim(),
      imapPort: parseInt(form.imapPort, 10),
      imapUser: form.imapUser.trim(),
      imapPassword: form.imapPassword,
      imapTls: form.imapTls,
      ...(canChooseOwner && ownerId ? { ownerId } : {}),
    };

    try {
      const result = await createMailbox.mutateAsync(data);
      let hadSmtpError = false;

      // If mailbox created and SMTP is shown, save SMTP settings
      if (showSmtp && smtpForm.smtpHost) {
        const mailboxId = result.data.id;
        if (mailboxId) {
          setCreatedMailboxId(mailboxId);
          try {
            await client.patch(`/mailboxes/${mailboxId}/smtp`, {
              smtpHost: smtpForm.smtpHost.trim(),
              smtpPort: parseInt(smtpForm.smtpPort, 10) || 587,
              smtpSecurityMode: smtpForm.smtpSecurityMode,
              smtpUser: smtpForm.smtpUser.trim(),
              smtpPassword: smtpForm.smtpPassword,
              displayName: smtpForm.displayName.trim() || null,
            });
          } catch (smtpErr) {
            hadSmtpError = true;
            setSmtpWarning(getErrorMessage(smtpErr) || 'Не удалось сохранить SMTP-настройки');
          }
        }
      }

      if (!hadSmtpError) {
        onSuccess();
      }
    } catch (err) {
      setErrors({ email: getErrorMessage(err) });
    }
  };

  const isGmail = form.email.toLowerCase().includes('@gmail.com');

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* SMTP save warning */}
      {smtpWarning && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-card border border-accent-warning/30 bg-accent-warning/5">
          <span className="text-base flex-shrink-0 mt-0.5">⚠️</span>
          <div className="flex-1">
            <p className="text-sm font-medium text-text-primary">Почтовый ящик создан, но SMTP не сохранён</p>
            <p className="text-sm text-text-secondary mt-1">{smtpWarning}</p>
            <p className="text-xs text-text-secondary mt-2">Вы можете настроить SMTP позже в настройках ящика.</p>
          </div>
          <button
            type="button"
            onClick={onSuccess}
            className="flex-shrink-0 px-3 py-1 text-sm text-accent-brand hover:underline"
          >
            Закрыть
          </button>
        </div>
      )}

      {/* Основная информация */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider">
          Основная информация
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Email"
            type="email"
            placeholder="mailbox@example.com"
            value={form.email}
            onChange={(v) => updateField('email', v)}
            disabled={createMailbox.isPending}
            {...(errors.email && { error: errors.email })}
          />
          <Input
            label="Название"
            placeholder="Мой ящик"
            value={form.name}
            onChange={(v) => updateField('name', v)}
            disabled={createMailbox.isPending}
            {...(errors.name && { error: errors.name })}
          />
        </div>
      </div>

      {/* Менеджер-владелец. Видно только владельцу и админу: оператор
          подключает ящик себе, и выбирать ему не из чего */}
      {canChooseOwner && (
        <div>
          <label className="block text-sm text-text-secondary mb-1.5" htmlFor="mailbox-owner">
            Менеджер
          </label>
          <select
            id="mailbox-owner"
            className="w-full bg-surface-subtle border border-border-subtle rounded-comp px-3 py-2 text-text-primary"
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            disabled={createMailbox.isPending}
          >
            <option value="">Мне ({user?.login})</option>
            {(staff ?? [])
              .filter((person) => person.id !== user?.id)
              .map((person) => (
                <option key={person.id} value={person.id}>
                  {person.login}
                </option>
              ))}
          </select>
          {staffFailed ? (
            <p className="text-xs text-accent-danger mt-1">
              Список менеджеров не загрузился — ящик будет подключён на вас.
              Передать его можно позже кнопкой «Передать» в карточке.
            </p>
          ) : (
            <p className="text-xs text-text-secondary mt-1">
              Диалоги этого ящика увидит выбранный менеджер. Вы видите все ящики организации.
            </p>
          )}
        </div>
      )}

      {/* Кнопка Gmail */}
      {isGmail && !form.imapHost && (
        <button
          type="button"
          onClick={applyGmailPreset}
          className="flex items-center gap-2 px-3 py-2 rounded-md bg-surface-subtle border border-border-subtle hover:border-accent-brand/50 text-text-secondary hover:text-text-primary transition-colors text-sm"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 010 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" />
          </svg>
          Применить настройки Gmail
        </button>
      )}

      {/* IMAP настройки */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider">
          Настройки IMAP
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="IMAP сервер"
            placeholder="imap.example.com"
            value={form.imapHost}
            onChange={(v) => updateField('imapHost', v)}
            disabled={createMailbox.isPending}
            {...(errors.imapHost && { error: errors.imapHost })}
          />
          <Input
            label="Порт"
            placeholder="993"
            value={form.imapPort}
            onChange={(v) => updateField('imapPort', v)}
            disabled={createMailbox.isPending}
            {...(errors.imapPort && { error: errors.imapPort })}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Пользователь"
            placeholder="mailbox@example.com"
            value={form.imapUser}
            onChange={(v) => updateField('imapUser', v)}
            disabled={createMailbox.isPending}
            {...(errors.imapUser && { error: errors.imapUser })}
          />
          <Input
            label="Пароль"
            type="password"
            placeholder="Пароль приложения или обычный"
            value={form.imapPassword}
            onChange={(v) => updateField('imapPassword', v)}
            disabled={createMailbox.isPending}
            {...(errors.imapPassword && { error: errors.imapPassword })}
          />
        </div>

        {/* TLS */}
        <label className="flex items-center gap-3 cursor-pointer group">
          <div className="relative">
            <input
              type="checkbox"
              checked={form.imapTls}
              onChange={(e) => updateField('imapTls', e.target.checked)}
              disabled={createMailbox.isPending}
              className="sr-only peer"
            />
            <div className="w-5 h-5 rounded border border-border-subtle bg-surface-subtle peer-checked:bg-accent-action peer-checked:border-accent-brand transition-colors peer-focus:ring-2 peer-focus:ring-accent-action/50 peer-focus:ring-offset-2 peer-focus:ring-offset-bg-primary" />
            <svg
              className="absolute top-0.5 left-0.5 w-4 h-4 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors">
            Использовать TLS/SSL шифрование
          </span>
        </label>
      </div>

      {/* Подсказка */}
      <div className="flex items-start gap-3 p-3 rounded-lg bg-surface-subtle border border-border-subtle">
        <svg
          className="w-5 h-5 text-text-secondary flex-shrink-0 mt-0.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
          />
        </svg>
        <p className="text-sm text-text-secondary">
          Для Gmail используйте{' '}
          <a
            href="https://support.google.com/accounts/answer/185833"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-brand hover:underline"
          >
            пароль приложения
          </a>
          {' '}вместо обычного пароля. Убедитесь, что IMAP включён в настройках Gmail.
        </p>
      </div>

      {/* SMTP Toggle */}
      <div className="border-t border-border-subtle pt-4">
        <button
          type="button"
          onClick={() => setShowSmtp(!showSmtp)}
          className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
        >
          <svg
            className={`w-4 h-4 transition-transform ${showSmtp ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          Настроить SMTP для отправки писем
          <span className="text-xs text-text-secondary">(опционально)</span>
        </button>

        {showSmtp && (
          <div className="mt-4">
            <SmtpSettings
              email={form.email}
              form={smtpForm}
              onChange={updateSmtpField}
              onTestConnection={handleTestSmtp}
              disabled={createMailbox.isPending}
              errors={errors}
            />
          </div>
        )}
      </div>

      {/* Кнопки */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <Button
          variant="ghost"
          size="md"
          onClick={onCancel}
          disabled={createMailbox.isPending}
          type="button"
        >
          Отмена
        </Button>
        <Button
          variant="primary"
          size="md"
          disabled={createMailbox.isPending}
          type="submit"
        >
          {createMailbox.isPending ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" />
              Добавление...
            </span>
          ) : (
            'Добавить ящик'
          )}
        </Button>
      </div>
    </form>
  );
}
